/**
 * CogsRollup.jsx - Reporting's "COGS & margin by project" (10/12/26): every
 * project with a budget, typed hours or expenses, side by side - budget sold
 * and cost, sold to date, COGS, profit and margin, what is left of the budget
 * cost, PO and invoiced (GET /api/project-costs/rollup). The 'cost-rates'
 * area only: ReportingView does not render it otherwise, and the server
 * refuses the call.
 */
import { useEffect, useState } from 'react';
import { SectionHeader } from './SectionHeader';
import { CARD } from './reportingTokens';
import { getCogsRollup, money, pct } from '../../components/costs/costApi';
import { exportCsv } from '../../utils/exportCsv';

const th = {
  padding: '9px 12px',
  textAlign: 'left',
  fontSize: 9.5,
  fontWeight: 700,
  color: '#5A5A5A',
  letterSpacing: 1.1,
  textTransform: 'uppercase',
  background: '#FAFBFC',
  borderBottom: '1px solid #F0F0F0',
  whiteSpace: 'nowrap',
};
const td = { padding: '8px 12px', fontSize: 11, color: '#333', fontVariantNumeric: 'tabular-nums' };
const tone = (m) => (m >= 0.3 ? '#1B5E20' : m >= 0.15 ? '#B07503' : '#B71C1C');

export default function CogsRollup({ onOpenProject }) {
  const [state, setState] = useState({ phase: 'loading' });
  useEffect(() => {
    let live = true;
    getCogsRollup().then((r) => {
      if (!live) return;
      setState(
        r.ok ? { phase: 'ready', ...r.body } : { phase: 'failed', error: (r.body && r.body.error) || 'Could not load the COGS roll-up.' }
      );
    });
    return () => {
      live = false;
    };
  }, []);
  const rows = state.projects || [];
  const T = state.totals;
  return (
    <>
      <SectionHeader
        title="COGS & Margin by Project"
        subtitle="Staff cost rates & COGS access only · projects with a budget, typed hours or expenses"
        explain={{
          title: 'COGS & margin',
          body: "Sold to date is the hours at each customer's level rates plus billable expenses; COGS is the hours at each person's burden rate plus every expense. The budget is the won Worksheet quote plus change orders.",
          formula: 'Margin = (Sold - COGS) / Sold',
        }}
        right={
          rows.length ? (
            <button
              type="button"
              onClick={() =>
                exportCsv(
                  'globalops_cogs_by_project.csv',
                  rows.map((r) => ({
                    'Quote #': r.quoteLetter,
                    Project: r.name,
                    Customer: r.customer,
                    'Budget sold': r.budgetSold,
                    'Budget cost': r.budgetCost,
                    Hours: r.hours,
                    Sold: r.sold,
                    COGS: r.cogs,
                    Profit: r.profit,
                    Margin: r.margin,
                    'Budget cost left': r.remainingCost,
                    'PO value': r.poValue,
                    Invoiced: r.invoiced,
                  }))
                )
              }
              style={{
                padding: '5px 12px',
                borderRadius: 6,
                border: '1px solid #E8E8E8',
                background: '#fff',
                fontSize: 10,
                fontWeight: 600,
                color: '#666',
                cursor: 'pointer',
                fontFamily: 'Montserrat',
              }}
            >
              Export CSV
            </button>
          ) : null
        }
      />
      <div style={{ ...CARD, padding: 0, marginBottom: 16 }}>
        {state.phase === 'loading' && <div style={{ padding: 16, fontSize: 11, color: '#888' }}>Loading...</div>}
        {state.phase === 'failed' && (
          <div role="alert" style={{ padding: 16, fontSize: 11, color: '#B71C1C', fontWeight: 600 }}>
            {state.error}
          </div>
        )}
        {state.phase === 'ready' && (
          <div style={{ overflowX: 'auto' }}>
            <table aria-label="COGS and margin by project" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {[
                    'Quote #',
                    'Project',
                    'Customer',
                    'Budget sold',
                    'Budget cost',
                    'Hours',
                    'Sold',
                    'COGS',
                    'Profit',
                    'Margin',
                    'Budget left',
                    'PO',
                    'Invoiced',
                  ].map((h) => (
                    <th key={h} style={th}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.id}
                    style={{ borderBottom: '1px solid #F7F7F7', cursor: onOpenProject ? 'pointer' : 'default' }}
                    onClick={() => onOpenProject && onOpenProject(r.id)}
                  >
                    <td style={{ ...td, fontWeight: 700 }}>{r.quoteLetter || '-'}</td>
                    <td style={{ ...td, fontWeight: 700, color: '#1B5E20', maxWidth: 240 }}>{r.name || '-'}</td>
                    <td style={td}>{r.customer}</td>
                    <td style={td}>{r.budgetSold == null ? '-' : money(r.budgetSold)}</td>
                    <td style={td}>{r.budgetCost == null ? '-' : money(r.budgetCost)}</td>
                    <td style={td}>{Number(r.hours || 0).toLocaleString('en-US', { maximumFractionDigits: 1 })}</td>
                    <td style={td}>{money(r.sold)}</td>
                    <td style={td}>{money(r.cogs)}</td>
                    <td style={td}>{money(r.profit)}</td>
                    <td style={{ ...td, fontWeight: 800, color: tone(r.margin) }}>{r.sold ? pct(r.margin) : '-'}</td>
                    <td style={{ ...td, color: r.remainingCost != null && r.remainingCost < 0 ? '#B71C1C' : '#333' }}>
                      {r.remainingCost == null ? '-' : money(r.remainingCost)}
                    </td>
                    <td style={td}>{money(r.poValue)}</td>
                    <td style={td}>{money(r.invoiced)}</td>
                  </tr>
                ))}
                {T && rows.length > 0 && (
                  <tr style={{ background: '#FAFBFC', borderTop: '2px solid #E8E8E8', fontWeight: 800 }}>
                    <td colSpan={3} style={{ ...td, fontWeight: 800, textAlign: 'right' }}>
                      Total ({rows.length})
                    </td>
                    <td style={td}>{money(T.budgetSold)}</td>
                    <td style={td}>{money(T.budgetCost)}</td>
                    <td style={td}>{Number(T.hours || 0).toLocaleString('en-US', { maximumFractionDigits: 1 })}</td>
                    <td style={td}>{money(T.sold)}</td>
                    <td style={td}>{money(T.cogs)}</td>
                    <td style={td}>{money(T.profit)}</td>
                    <td style={{ ...td, color: tone(T.margin) }}>{pct(T.margin)}</td>
                    <td style={td} />
                    <td style={td}>{money(T.poValue)}</td>
                    <td style={td}>{money(T.invoiced)}</td>
                  </tr>
                )}
              </tbody>
            </table>
            {!rows.length && (
              <div style={{ padding: 16, fontSize: 11, color: '#888' }}>
                No project has a budget, typed hours or expenses yet. Start one on a project's Budget tab.
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}
